/**
 * Gmail's single-key mail shortcuts (core logic/shortcuts `mailShortcut`) while the mail pane is focused: never while
 * typing in a field, with ⌘ / Ctrl / ⌥ held, or with a dialog or menu open. `onShortcut` returns whether it used it.
 */
import { Shortcuts } from "@bundle/core";
import { useEffect, useRef } from "react";

function typing(t: EventTarget | null): boolean {
  const el = t as HTMLElement | null;
  return el !== null && (el.isContentEditable || el.tagName === "INPUT" || el.tagName === "TEXTAREA" || el.tagName === "SELECT");
}

export function useMailShortcuts(enabled: boolean, onShortcut: (s: Shortcuts.MailShortcut) => boolean): void {
  const latest = useRef(onShortcut);
  useEffect(() => {
    latest.current = onShortcut;
  });
  useEffect(() => {
    if (!enabled) return undefined;
    const onKey = (e: KeyboardEvent) => {
      if (e.defaultPrevented || e.metaKey || e.ctrlKey || e.altKey || typing(e.target)) return;
      if (document.querySelector('[role="dialog"], [role="menu"], [role="listbox"]') !== null) return;
      const s = Shortcuts.mailShortcut(e.key, e.shiftKey);
      if (s !== undefined && latest.current(s)) e.preventDefault();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [enabled]);
}
